// client/src/components/pages/Geofencing/TestAgainstHistory.js
// "Test against history" (FR-GEO-014, LS-09 §5.10): how many entry / exit
// events the zone as currently drawn would have produced over a past window.
// Calls POST /api/geofences/:id/test-against-history with the draft geometry.
// The server counts raw containment transitions (engine debounce not applied).
import React, { useState } from 'react';
import { FaHistory, FaSpinner } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import { toApiDateString, formatAppDateTime } from '../../../utils/dateUtils';
import { testGeofenceAgainstHistory } from './geofenceConfigApi';

const DAY_MS = 86400000;

/** The picked days as a SAST [from, to) window: from 00:00 to the day after `end`. */
export function historyWindowFor(start, end) {
  const next = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
  return {
    from: `${toApiDateString(start)}T00:00:00+02:00`,
    to: `${toApiDateString(next)}T00:00:00+02:00`,
  };
}

/**
 * @param {number|null} geofenceId  saved zone id, or null for an unsaved one
 * @param {object|null} draft       geometry part of the save payload, or null when
 *                                  nothing is drawn yet
 */
export default function TestAgainstHistory({ geofenceId, draft }) {
  const [start, setStart] = useState(() => new Date(Date.now() - 7 * DAY_MS));
  const [end, setEnd] = useState(() => new Date());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      setResult(await testGeofenceAgainstHistory(geofenceId, draft, historyWindowFor(start, end)));
    } catch (err) {
      setResult(null);
      setError(err.response?.data?.error || err.message || 'Test failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[14rem]">
          <UnifiedDateRangePicker
            startDate={start}
            endDate={end}
            onStartDateChange={setStart}
            onEndDateChange={setEnd}
          />
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running || !draft}
          title={draft ? undefined : 'Draw the zone first'}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors disabled:opacity-50"
        >
          {running ? <FaSpinner className="animate-spin" size={12} /> : <FaHistory size={12} />}
          Test against history
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-3" data-testid="history-result">
          <p className="text-sm text-gray-800">
            <span className="font-semibold tabular-nums">{result.entries}</span> entries ·{' '}
            <span className="font-semibold tabular-nums">{result.exits}</span> exits ·{' '}
            <span className="tabular-nums">{result.assets_with_events}</span> vehicle
            {result.assets_with_events === 1 ? '' : 's'}
            <span className="text-gray-500">
              {' '}
              — {result.fixes_scanned.toLocaleString()} fixes,{' '}
              {formatAppDateTime(result.window?.from)} to {formatAppDateTime(result.window?.to)}
            </span>
          </p>
          {result.truncated && (
            <p className="text-xs text-amber-700 mt-1">
              Stopped at {result.max_fixes?.toLocaleString()} fixes — narrow the window for a full
              count.
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Upper bound: raw crossings between consecutive fixes; the engine's debounce is not
            applied.
          </p>
          {result.by_asset?.length > 0 && (
            <ul className="mt-2 text-xs text-gray-600 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
              {result.by_asset.map((a) => (
                <li key={a.asset_key} className="flex justify-between gap-3">
                  <span className="font-mono truncate">{a.asset_key}</span>
                  <span className="tabular-nums">
                    {a.entries} in / {a.exits} out
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
